/**
 * Guarded web-storage access.
 *
 * In privacy configurations (blocked cookies/storage, some embedded webviews)
 * merely TOUCHING window.sessionStorage/localStorage throws a SecurityError.
 * Unguarded module-init access (the old redux/store.js pattern) white-screens
 * the whole app for those visitors, and a throwing effect unmounts the React
 * tree. Every storage read/write goes through these helpers: failures degrade
 * to "no storage" instead of crashing.
 */

/** @param {"session" | "local"} kind */
const backend = (kind) => {
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

/** @param {string} key @param {"session" | "local"} [kind] @returns {string | null} */
export const storageGet = (key, kind = "session") => {
  try {
    return backend(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

/** @param {string} key @param {string} value @param {"session" | "local"} [kind] */
export const storageSet = (key, value, kind = "session") => {
  try {
    backend(kind)?.setItem(key, value);
  } catch {
    /* storage unavailable — feature degrades, app survives */
  }
};

/** @param {string} key @param {"session" | "local"} [kind] */
export const storageRemove = (key, kind = "session") => {
  try {
    backend(kind)?.removeItem(key);
  } catch {
    /* storage unavailable */
  }
};
